import React, { useEffect, useRef, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useWebAuth } from "@/lib/webAuth";
import webApi from "@/lib/webApi";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Sheet, SheetContent, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuLabel,
} from "@/components/ui/dropdown-menu";
import {
  Send,
  Eye,
  Image as ImageIcon,
  Paperclip,
  Mic,
  MicOff,
  Sparkles,
  ShieldCheck,
  ShoppingCart,
  LogOut,
  Loader2,
  Coins,
  ListOrdered,
  Menu as MenuIcon,
  UserCircle,
  Search,
  Lock,
  Terminal,
  Monitor,
  HelpCircle,
  Flag,
  Download,
  Bell,
  BellOff,
  X,
  FileText,
  LogIn,
  UserPlus,
  Home,
  ChevronRight,
  AlertCircle,
  Bot,
  StopCircle,
  Share2,
  FileImage,
  FileDown,
} from "lucide-react";
import { toast } from "sonner";
import { usePWAInstall } from "@/hooks/usePWAInstall";
import { usePushNotifications } from "@/hooks/usePushNotifications";
import useVoiceDictation from "@/hooks/useVoiceDictation";
import useAssistantVoice from "@/hooks/useAssistantVoice";
import CodeRain from "@/components/effects/CodeRain";
import LinkTelegramCard from "@/components/web/LinkTelegramCard";

// ----------------------- Bubble (Cyber-Gothic Electro-Punk) -----------------------
// `streaming` enables a typewriter effect for AI replies — chars are revealed
// progressively at ~20 chars per 30ms so it feels like real token streaming.
function Bubble({ role, kind, content, image_b64, image_mime, filename, streaming }) {
  const isUser = role === "user";
  const [revealed, setRevealed] = useState(streaming ? "" : content);

  useEffect(() => {
    if (!streaming) {
      setRevealed(content);
      return;
    }
    setRevealed("");
    const full = content || "";
    let i = 0;
    const CHUNK = 3; // chars per tick — snappy but readable
    const INTERVAL = 18; // ms per tick
    const id = setInterval(() => {
      i = Math.min(full.length, i + CHUNK);
      setRevealed(full.slice(0, i));
      if (i >= full.length) clearInterval(id);
    }, INTERVAL);
    return () => clearInterval(id);
  }, [content, streaming]);

  const isStreaming = streaming && revealed.length < (content || "").length;

  return (
    <div
      className={`flex ${isUser ? "justify-end" : "justify-start"} mb-5`}
      data-testid={`bubble-${role}`}
    >
      <div
        className={`max-w-[88%] sm:max-w-[78%] px-4 py-3 font-sans text-[14px] leading-relaxed relative
          ${
            isUser
              ? "liquid-metallic text-white shadow-[0_8px_24px_rgba(10,68,255,0.35)] hover-sparkle rounded-2xl rounded-tr-md"
              : "glass-panel text-zinc-100 hover-sparkle rounded-2xl rounded-tl-md"
          }`}
      >
        {/* Top label strip */}
        <div className={`absolute -top-2 ${isUser ? "right-3" : "left-3"} px-2 py-0.5 text-[9px] font-cyber font-bold tracking-widest ${
          isUser ? "bg-[#00E5FF] text-black" : "bg-white text-black"
        }`}>
          {isUser ? "TÚ" : "// IARISK"}
        </div>

        {filename && (
          <div className="flex items-center gap-2 mb-2 text-[10px] font-cyber tracking-wider text-[#00E5FF]/80">
            <FileText size={13} />
            <span className="truncate">{filename}</span>
          </div>
        )}
        {image_b64 ? (
          <img
            src={`data:${image_mime || "image/jpeg"};base64,${image_b64}`}
            alt="generated"
            className="max-w-full mb-2 border border-[#00E5FF]/40 shadow-[0_0_24px_rgba(0,229,255,0.3)]"
          />
        ) : null}
        <div
          className="text-sm whitespace-pre-wrap break-words"
          style={{ overflowWrap: "anywhere" }}
        >
          {revealed}
          {isStreaming && (
            <span
              className="inline-block w-[7px] h-[14px] bg-[#00E5FF] ml-0.5 align-text-bottom animate-pulse"
              style={{ boxShadow: "0 0 6px #00E5FF" }}
              data-testid="stream-caret"
              aria-hidden="true"
            />
          )}
        </div>
        {kind && kind !== "text" && (
          <div className="text-[9px] mt-2 text-[#00E5FF]/60 font-cyber tracking-widest">
            ⚡ {kind}
          </div>
        )}
      </div>
    </div>
  );
}

// ----------------------- Modes -----------------------
const MODES = [
  { id: "chat", label: "Chat IA", icon: Send, color: "text-[#2AABEE]", desc: "Conversa con Claude Sonnet 4.5" },
  { id: "image_gen", label: "Generar imagen", icon: Sparkles, color: "text-violet-400", desc: "Crea imágenes con texto" },
  { id: "scan", label: "Escanear URL", icon: ShieldCheck, color: "text-emerald-400", desc: "Analiza si un link es seguro" },
];

// Side menu items — these are the bot's full capabilities, exposed in the web app.
const MENU_GROUPS = [
  {
    label: "Asistente",
    items: [
      { id: "mode:chat", label: "Chat IA", icon: Send },
      { id: "mode:image_gen", label: "Generar imagen", icon: Sparkles },
      { id: "mode:scan", label: "Escanear URL", icon: ShieldCheck },
      { id: "action:upload-image", label: "Analizar imagen", icon: ImageIcon },
      { id: "action:upload-doc", label: "Leer archivo / PDF", icon: FileText },
      { id: "action:upload-voice", label: "Subir audio", icon: Mic },
    ],
  },
  {
    label: "Cuenta",
    items: [
      { id: "nav:/comprar", label: "Comprar créditos", icon: ShoppingCart },
      { id: "nav:/ordenes", label: "Mis órdenes", icon: ListOrdered },
    ],
  },
  {
    label: "Aprende (en Telegram)",
    items: [
      { id: "tg:kali", label: "Linux / Kali", icon: Terminal, hint: "/kali" },
      { id: "tg:windows", label: "Comandos Windows", icon: Monitor, hint: "/windows" },
      { id: "tg:privacidad", label: "Privacidad / Tor / VPN", icon: Lock, hint: "/privacidad" },
      { id: "tg:buscar", label: "Búsqueda web", icon: Search, hint: "/buscar" },
    ],
  },
];

// ----------------------- Sidebar (left) -----------------------
function LeftSidebarContent({ user, mode, setMode, triggerUpload, navigate, onClose, botUsername, onTelegramLinked }) {
  const handleClick = (id) => {
    if (id.startsWith("mode:")) {
      setMode(id.split(":")[1]);
    } else if (id.startsWith("action:")) {
      triggerUpload(id.split(":")[1]);
    } else if (id.startsWith("nav:")) {
      navigate(id.split(":")[1]);
    } else if (id.startsWith("tg:")) {
      const cmd = id.split(":")[1];
      window.open(`https://t.me/${botUsername}?start=${cmd}`, "_blank");
    } else if (id.startsWith("download:")) {
      const platform = id.split(":")[1];
      const API = process.env.REACT_APP_BACKEND_URL;
      window.location.href = `${API}/api/download/${platform}`;
    }
    onClose?.();
  };

  return (
    <div className="h-full flex flex-col bg-black text-white relative">
      {/* Vertical accent strip */}
      <div className="absolute left-0 top-0 bottom-0 w-[3px] bg-gradient-to-b from-[#00E5FF] via-transparent to-[#0044FF] opacity-80" />

      {/* Brand */}
      <div className="h-16 flex items-center gap-3 px-5 border-b border-rune-red/15 relative">
        <div
          className="w-9 h-9 flex items-center justify-center flex-shrink-0 clip-rune border border-rune-red/50"
          style={{
            background: "radial-gradient(ellipse at 30% 30%, #ff1a3a 0%, #b8001f 45%, #220006 100%)",
            boxShadow: "0 0 18px rgba(255, 26, 58, 0.5), inset 0 0 8px rgba(0,0,0,0.5)",
          }}
        >
          <Eye size={16} className="text-white" strokeWidth={2.5} />
        </div>
        <div>
          <div
            className="font-runic text-base tracking-[0.25em] text-white"
            style={{ textShadow: "0 0 12px rgba(255,26,58,0.45)" }}
          >
            IARISK
          </div>
          <div className="text-[9px] font-mono tracking-[0.3em] text-data-cyan/70 -mt-0.5">
            EL OJO · WEB
          </div>
        </div>
      </div>

      {/* User card */}
      <div className="px-5 py-4 border-b border-rune-red/15">
        <div className="text-[9px] font-mono tracking-widest text-zinc-500 mb-2">SESIÓN ACTIVA</div>
        <div className="flex items-center gap-3">
          <div
            className="w-9 h-9 flex items-center justify-center rounded-sm border border-data-cyan/30"
            style={{
              background: "linear-gradient(135deg, rgba(0,30,40,0.7) 0%, rgba(2,8,12,0.85) 100%)",
              boxShadow: "0 0 16px rgba(0, 224, 255, 0.2)",
            }}
          >
            <UserCircle size={20} className="text-data-cyan" />
          </div>
          <div className="min-w-0 flex-1">
            <div className="text-sm font-bold truncate text-white">
              {user.first_name || user.username || "Usuario"}
            </div>
            <div className="text-[10px] text-zinc-400 truncate font-mono">
              @{user.username || user.telegram_id}
            </div>
          </div>
        </div>
        <div className="mt-3 space-y-1.5">
          <div
            className="flex items-center justify-between rounded-sm px-3 py-2 border border-rune-red/25 transition-colors hover:border-rune-red/60"
            style={{ background: "rgba(40, 0, 10, 0.4)" }}
            data-testid="sidebar-credits-paid"
            title="Bolsa B · Compras y referidos. Sirve para TODO incluido clonador y OSINT."
          >
            <span className="text-[10px] font-mono tracking-widest text-rune-red">CRÉDITOS</span>
            <span
              className="text-base font-mono font-bold flex items-center gap-1.5 text-white"
              style={{ textShadow: "0 0 10px rgba(255,26,58,0.5)" }}
            >
              <Coins size={14} className="text-rune-red" />
              {user.credits ?? 0}
            </span>
          </div>
          {(user.bonus_credits ?? 0) > 0 && (
            <div
              className="flex items-center justify-between rounded-sm px-3 py-2 border border-data-cyan/30"
              style={{ background: "rgba(0, 30, 40, 0.4)" }}
              data-testid="sidebar-credits-bonus"
              title="Bolsa A · Bono de bienvenida. Sirve para chat, imagen, voz, búsqueda. NO sirve para clonador / OSINT premium."
            >
              <span className="text-[10px] font-mono tracking-widest text-data-cyan">BONO</span>
              <span
                className="text-base font-mono font-bold flex items-center gap-1.5 text-data-cyan"
                style={{ textShadow: "0 0 10px rgba(0,224,255,0.5)" }}
              >
                <Sparkles size={14} />
                {user.bonus_credits ?? 0}
              </span>
            </div>
          )}
        </div>
      </div>

      {/* Optional: link Telegram account */}
      <LinkTelegramCard user={user} onLinked={onTelegramLinked} />

      {/* Menu groups */}
      <div className="flex-1 overflow-y-auto py-3">
        {MENU_GROUPS.map((g) => (
          <div key={g.label} className="px-3 py-2">
            <div className="px-3 py-1.5 text-[9px] font-mono tracking-[0.25em] text-zinc-500 font-bold flex items-center gap-2">
              <span className="w-2 h-px bg-rune-red" />
              {g.label}
            </div>
            {g.items.map((it) => {
              const Icon = it.icon;
              const isActive =
                it.id.startsWith("mode:") &&
                it.id.split(":")[1] === mode;
              return (
                <button
                  key={it.id}
                  onClick={() => handleClick(it.id)}
                  data-testid={`sidebar-${it.id.replace(/[:/]/g, "-")}`}
                  className={`w-full flex items-center gap-3 px-3 py-2.5 text-[13px] font-sans tracking-wide text-left transition-all duration-200 rounded-sm group ${
                    isActive
                      ? "text-white font-bold border-l-2 border-rune-red"
                      : "text-zinc-300 hover:bg-rune-red/5 hover:text-white border-l-2 border-transparent hover:border-rune-red/60"
                  }`}
                  style={
                    isActive
                      ? {
                          background:
                            "linear-gradient(90deg, rgba(255,26,58,0.18) 0%, rgba(255,26,58,0.04) 100%)",
                          boxShadow:
                            "inset 0 0 12px rgba(255,26,58,0.15)",
                        }
                      : undefined
                  }
                >
                  <Icon size={15} className="flex-shrink-0" />
                  <span className="flex-1 truncate">{it.label}</span>
                  {it.hint ? (
                    <span className={`text-[9px] font-mono ${isActive ? "text-rune-red/80" : "text-zinc-500"}`}>
                      {it.hint}
                    </span>
                  ) : null}
                  {isActive && <ChevronRight size={13} className="text-rune-red" />}
                </button>
              );
            })}
          </div>
        ))}
      </div>
    </div>
  );
}

// ----------------------- Account dropdown (right) -----------------------
function AccountMenu({ user, logout, navigate }) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          size="sm"
          variant="ghost"
          className="text-zinc-300 hover:text-zinc-100 rounded-sm h-9 px-2 gap-1.5"
          data-testid="account-menu-btn"
        >
          <UserCircle size={18} />
          <span className="hidden sm:inline text-xs font-bold truncate max-w-[110px]">
            {user.first_name || user.username || "Cuenta"}
          </span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        className="w-56 bg-zinc-950 border border-zinc-800 text-zinc-100"
        data-testid="account-menu-content"
      >
        <DropdownMenuLabel className="text-xs text-zinc-500">
          Cuenta
        </DropdownMenuLabel>
        <DropdownMenuItem
          className="text-sm cursor-pointer focus:bg-zinc-900"
          onSelect={() => navigate("/chat")}
          data-testid="menu-home"
        >
          <Home size={14} className="mr-2 text-zinc-400" />
          Inicio
        </DropdownMenuItem>
        <DropdownMenuItem
          className="text-sm cursor-pointer focus:bg-zinc-900"
          onSelect={() => navigate("/comprar")}
          data-testid="menu-buy"
        >
          <ShoppingCart size={14} className="mr-2 text-zinc-400" />
          Comprar créditos
        </DropdownMenuItem>
        <DropdownMenuItem
          className="text-sm cursor-pointer focus:bg-zinc-900"
          onSelect={() => navigate("/ordenes")}
          data-testid="menu-orders"
        >
          <ListOrdered size={14} className="mr-2 text-zinc-400" />
          Mis órdenes
        </DropdownMenuItem>
        <DropdownMenuSeparator className="bg-zinc-800" />
        <DropdownMenuLabel className="text-xs text-zinc-500">
          Soporte
        </DropdownMenuLabel>
        <DropdownMenuItem
          className="text-sm cursor-pointer focus:bg-zinc-900"
          onSelect={() => window.open("https://t.me/IARisk_bot?start=ayuda", "_blank")}
          data-testid="menu-help"
        >
          <HelpCircle size={14} className="mr-2 text-zinc-400" />
          Ayuda
        </DropdownMenuItem>
        <DropdownMenuItem
          className="text-sm cursor-pointer focus:bg-zinc-900"
          onSelect={() => window.open("https://t.me/IARisk_bot?start=reportar", "_blank")}
          data-testid="menu-report"
        >
          <Flag size={14} className="mr-2 text-zinc-400" />
          Reportar problema
        </DropdownMenuItem>
        <DropdownMenuSeparator className="bg-zinc-800" />
        <DropdownMenuItem
          className="text-sm cursor-pointer focus:bg-red-950/40 text-red-400"
          onSelect={logout}
          data-testid="menu-logout"
        >
          <LogOut size={14} className="mr-2" />
          Cerrar sesión
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

// ----------------------- Anonymous header (logged out preview) -----------------------
function AnonAccountMenu({ navigate }) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          size="sm"
          variant="ghost"
          className="text-zinc-300 hover:text-zinc-100 rounded-sm h-9 px-2 gap-1.5"
          data-testid="anon-account-menu-btn"
        >
          <UserCircle size={18} />
          <span className="hidden sm:inline text-xs font-bold">Cuenta</span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        className="w-56 bg-zinc-950 border border-zinc-800 text-zinc-100"
      >
        <DropdownMenuLabel className="text-xs text-zinc-500">
          Acceder
        </DropdownMenuLabel>
        <DropdownMenuItem
          className="text-sm cursor-pointer focus:bg-zinc-900"
          onSelect={() => navigate("/")}
          data-testid="menu-login"
        >
          <LogIn size={14} className="mr-2 text-zinc-400" />
          Iniciar sesión
        </DropdownMenuItem>
        <DropdownMenuItem
          className="text-sm cursor-pointer focus:bg-zinc-900"
          onSelect={() => navigate("/")}
          data-testid="menu-register"
        >
          <UserPlus size={14} className="mr-2 text-zinc-400" />
          Crear cuenta
        </DropdownMenuItem>
        <DropdownMenuSeparator className="bg-zinc-800" />
        <DropdownMenuItem
          className="text-sm cursor-pointer focus:bg-zinc-900"
          onSelect={() => window.open("https://t.me/IARisk_bot?start=ayuda", "_blank")}
        >
          <HelpCircle size={14} className="mr-2 text-zinc-400" />
          Ayuda
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

// ----------------------- Main component -----------------------
export default function WebChat() {
  const { user, loading, logout, refreshCredits } = useWebAuth();
  const navigate = useNavigate();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [mode, setMode] = useState("chat");
  const [dragOver, setDragOver] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [botUsername, setBotUsername] = useState("IARisk_bot");
  const fileInputRef = useRef(null);
  const docInputRef = useRef(null);
  const audioInputRef = useRef(null);
  const bottomRef = useRef(null);
  const composerRef = useRef(null);
  const messagesRef = useRef(null);
  const [exporting, setExporting] = useState(false);
  const { canInstall, isStandalone, promptInstall } = usePWAInstall();
  const { supported: pushSupported, permission: pushPerm, subscribed: pushOn, enabling: pushEnabling, enable: pushEnable, disable: pushDisable } = usePushNotifications();
  const {
    supported: dictateSupported,
    recording: dictating,
    uploading: dictateUploading,
    level: dictateLevel,
    start: dictateStart,
    stop: dictateStop,
  } = useVoiceDictation();

  // Voice assistant (push-to-talk + TTS) — premium feature
  const assistant = useAssistantVoice({
    onResult: (d) => {
      if (d?.error) {
        if (d.status === 402) {
          toast.error("Asistente premium: 5 créditos por uso. Compra créditos.");
          navigate("/comprar");
        } else {
          toast.error(d.error || "Error en el asistente de voz");
        }
        return;
      }
      // Append the transcript + reply to the chat thread.
      const ts = Date.now();
      setMessages((m) => [
        ...m,
        {
          id: `av-u-${ts}`,
          role: "user",
          content: `🎤 ${d.transcript || ""}`,
          kind: "voice",
        },
        {
          id: `av-a-${ts + 1}`,
          role: "assistant",
          content: d.reply || "",
          kind: "text",
        },
      ]);
      refreshCredits?.();
    },
  });

  // Hold-to-talk wrappers (mouse + touch). We start on press and stop on
  // release. Pointer cancel/leave also stops, so accidental drags don't
  // leave the recorder hanging.
  const ptStart = useCallback(
    (e) => {
      e.preventDefault?.();
      assistant.start();
    },
    [assistant],
  );
  const ptStop = useCallback(
    (e) => {
      e.preventDefault?.();
      assistant.stop();
    },
    [assistant],
  );

  // Optional deep-link: /chat?assistant=1 shows a one-time hint.
  useEffect(() => {
    if (typeof window === "undefined") return;
    const p = new URLSearchParams(window.location.search);
    if (p.get("assistant") === "1") {
      toast.message(
        "🎤 Asistente de voz: mantén presionado el botón 🤖 y habla.",
        { duration: 6000 },
      );
    }
  }, []);

  const handleDictateClick = useCallback(() => {
    if (dictating) {
      dictateStop();
      return;
    }
    if (!dictateSupported) {
      // Fallback to file upload picker for older browsers / blocked mic
      audioInputRef.current?.click();
      return;
    }
    dictateStart((text, meta) => {
      if (meta?.error) {
        toast.error("No se pudo acceder al micrófono. Revisa permisos o usa subir audio.");
        return;
      }
      if (!text) {
        toast.message("No se detectó voz clara, intenta de nuevo.");
        return;
      }
      // Insert the dictated text at the end of whatever is in the input
      setInput((prev) => (prev ? `${prev.trim()} ${text}` : text));
      toast.success(`🎙️ Dictado · ${text.length} caracteres${meta?.cost ? ` · -${meta.cost} cr` : ""}`);
    });
  }, [dictating, dictateSupported, dictateStart, dictateStop]);

  useEffect(() => {
    if (!loading && !user) {
      navigate("/", { replace: true });
    }
  }, [user, loading, navigate]);

  useEffect(() => {
    webApi.get("/public/info").then((r) => {
      if (r.data?.bot_username) setBotUsername(r.data.bot_username);
    }).catch(() => null);
  }, []);

  useEffect(() => {
    if (!user) return;
    webApi.get("/messages?limit=80").then((r) => {
      setMessages(
        r.data.map((m) => ({
          id: m.id,
          role: m.role,
          kind: m.kind,
          content: m.content,
        }))
      );
    });
  }, [user]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const triggerUpload = useCallback((kind) => {
    if (kind === "upload-image") fileInputRef.current?.click();
    else if (kind === "upload-doc") docInputRef.current?.click();
    else if (kind === "upload-voice") audioInputRef.current?.click();
  }, []);

  // ------------- Send text/chat -------------
  const send = async () => {
    const text = input.trim();
    if (!text) return;
    setInput("");
    setSending(true);
    const myId = `tmp-${Date.now()}`;
    setMessages((m) => [...m, { id: myId, role: "user", content: text, kind: "text" }]);
    try {
      if (mode === "image_gen") {
        const r = await webApi.post("/image/generate", { prompt: text });
        setMessages((m) => [
          ...m,
          {
            id: `img-${Date.now()}`,
            role: "assistant",
            content: `Imagen generada para: ${text}`,
            kind: "image",
            image_b64: r.data.image_b64,
            image_mime: r.data.mime || "image/jpeg",
          },
        ]);
        await refreshCredits();
      } else if (mode === "scan") {
        const r = await webApi.post("/scan", { url: text });
        const v = r.data.verdict;
        const emoji =
          v.level === "malicious" ? "🚫" : v.level === "suspicious" ? "⚠️" : "✅";
        const reasons = (v.reasons || []).map((x) => `• ${x}`).join("\n") || "—";
        setMessages((m) => [
          ...m,
          {
            id: `scan-${Date.now()}`,
            role: "assistant",
            content: `${emoji} ${v.level.toUpperCase()} · ${v.threat || "ok"}\n\n${reasons}`,
            kind: "scan",
          },
        ]);
        await refreshCredits();
      } else {
        const r = await webApi.post("/chat", { text });
        setMessages((m) => [
          ...m,
          {
            id: `r-${Date.now()}`,
            role: "assistant",
            content: r.data.reply,
            kind: "text",
          },
        ]);
        await refreshCredits();
      }
    } catch (e) {
      const msg = e?.response?.data?.detail || "Error procesando tu mensaje";
      if (e?.response?.status === 402) {
        toast.error("Sin créditos suficientes. Compra para continuar.");
        navigate("/comprar");
      } else {
        toast.error(msg);
      }
      setMessages((m) => m.filter((x) => x.id !== myId));
    } finally {
      setSending(false);
    }
  };

  // ------------- File handlers -------------
  const uploadImage = useCallback(
    async (file) => {
      if (!file) return;
      setSending(true);
      setMessages((m) => [
        ...m,
        {
          id: `up-${Date.now()}`,
          role: "user",
          content: input || "📸 Imagen",
          kind: "image_in",
          filename: file.name || "imagen",
        },
      ]);
      try {
        const fd = new FormData();
        fd.append("file", file);
        fd.append("prompt", input || "");
        setInput("");
        const r = await webApi.post("/image/analyze", fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        setMessages((m) => [
          ...m,
          {
            id: `vr-${Date.now()}`,
            role: "assistant",
            content: r.data.reply,
            kind: "text",
          },
        ]);
        await refreshCredits();
      } catch (err) {
        const msg = err?.response?.data?.detail || "Error analizando la imagen";
        if (err?.response?.status === 402) {
          toast.error("Sin créditos suficientes.");
          navigate("/comprar");
        } else {
          toast.error(msg);
        }
      } finally {
        setSending(false);
      }
    },
    [input, navigate, refreshCredits]
  );

  const uploadDoc = useCallback(
    async (file) => {
      if (!file) return;
      setSending(true);
      setMessages((m) => [
        ...m,
        {
          id: `dc-${Date.now()}`,
          role: "user",
          content: input || `📄 Análisis del archivo`,
          kind: "doc_in",
          filename: file.name,
        },
      ]);
      try {
        const fd = new FormData();
        fd.append("file", file);
        fd.append("prompt", input || "");
        setInput("");
        const r = await webApi.post("/document", fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        setMessages((m) => [
          ...m,
          {
            id: `dr-${Date.now()}`,
            role: "assistant",
            content: r.data.reply,
            kind: "text",
          },
        ]);
        await refreshCredits();
      } catch (err) {
        const msg = err?.response?.data?.detail || "Error analizando el archivo";
        if (err?.response?.status === 402) {
          toast.error("Sin créditos suficientes.");
          navigate("/comprar");
        } else if (err?.response?.status === 415) {
          toast.error("Formato no soportado. Prueba PDF, DOCX, TXT o código.");
        } else {
          toast.error(msg);
        }
      } finally {
        setSending(false);
      }
    },
    [input, navigate, refreshCredits]
  );

  const uploadAudio = useCallback(
    async (file) => {
      if (!file) return;
      setSending(true);
      setMessages((m) => [
        ...m,
        {
          id: `au-${Date.now()}`,
          role: "user",
          content: `🎤 Audio: ${file.name}`,
          kind: "voice",
          filename: file.name,
        },
      ]);
      try {
        const fd = new FormData();
        fd.append("file", file);
        const r = await webApi.post("/voice", fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        setMessages((m) => [
          ...m,
          {
            id: `tr-${Date.now()}`,
            role: "user",
            content: `📝 Transcripción: "${r.data.transcript}"`,
            kind: "transcript",
          },
          {
            id: `vr-${Date.now()}`,
            role: "assistant",
            content: r.data.reply,
            kind: "text",
          },
        ]);
        await refreshCredits();
      } catch (err) {
        const msg = err?.response?.data?.detail || "Error procesando el audio";
        if (err?.response?.status === 402) {
          toast.error("Sin créditos suficientes.");
          navigate("/comprar");
        } else {
          toast.error(msg);
        }
      } finally {
        setSending(false);
      }
    },
    [navigate, refreshCredits]
  );

  // ------------- Share conversation (PNG / PDF) -------------
  // Wraps html2canvas + jspdf. We render the messages column to a canvas,
  // stamp an IARISK header (brand + date + user), and offer download.
  const exportChat = useCallback(
    async (format /* "png" | "pdf" */) => {
      if (!messagesRef.current) return;
      if (!messages || messages.length === 0) {
        toast.error("La conversación está vacía.");
        return;
      }
      setExporting(true);
      try {
        const { default: html2canvas } = await import("html2canvas");
        const node = messagesRef.current;
        // Snapshot with a slightly wider background so bubble shadows aren't clipped.
        const canvas = await html2canvas(node, {
          backgroundColor: "#02040A",
          scale: window.devicePixelRatio > 1 ? 2 : 1.5,
          useCORS: true,
          logging: false,
          windowWidth: node.scrollWidth,
          windowHeight: node.scrollHeight,
        });

        // Compose a branded PNG with header + footer.
        const header = 90;
        const footer = 44;
        const out = document.createElement("canvas");
        out.width = canvas.width;
        out.height = canvas.height + header + footer;
        const ctx = out.getContext("2d");
        ctx.fillStyle = "#02040A";
        ctx.fillRect(0, 0, out.width, out.height);
        // Header
        ctx.fillStyle = "#FF1A3A";
        ctx.fillRect(0, 0, out.width, 4);
        ctx.fillStyle = "#FFFFFF";
        ctx.font = "bold 34px Cinzel, serif";
        ctx.fillText("IARISK", 32, 52);
        ctx.fillStyle = "#00E0FF";
        ctx.font = "16px monospace";
        ctx.fillText("// EL OJO QUE TODO LO VE", 200, 52);
        ctx.fillStyle = "#a1a1aa";
        ctx.font = "14px monospace";
        const now = new Date().toLocaleString("es-MX");
        const uname = user?.first_name || user?.username || user?.email || "usuario";
        ctx.fillText(`${uname} · ${now}`, 32, 76);
        // Body
        ctx.drawImage(canvas, 0, header);
        // Footer
        ctx.fillStyle = "#00E0FF";
        ctx.fillRect(0, out.height - footer, out.width, 2);
        ctx.fillStyle = "#71717a";
        ctx.font = "12px monospace";
        ctx.fillText("iarisk.com · Chat exportado desde el bot", 32, out.height - 18);

        const ts = new Date().toISOString().replace(/[:.]/g, "-").slice(0, 19);

        if (format === "png") {
          out.toBlob(
            (blob) => {
              if (!blob) {
                toast.error("No se pudo generar la imagen.");
                return;
              }
              const url = URL.createObjectURL(blob);
              const a = document.createElement("a");
              a.href = url;
              a.download = `iarisk-chat-${ts}.png`;
              a.click();
              setTimeout(() => URL.revokeObjectURL(url), 2000);
              toast.success("Imagen guardada");
            },
            "image/png",
            0.92,
          );
        } else {
          const { default: jsPDF } = await import("jspdf");
          const pdf = new jsPDF({
            orientation: "portrait",
            unit: "pt",
            format: "a4",
          });
          const pageW = pdf.internal.pageSize.getWidth();
          const pageH = pdf.internal.pageSize.getHeight();
          const imgW = pageW - 40;
          const imgH = (out.height * imgW) / out.width;

          const imgData = out.toDataURL("image/jpeg", 0.9);
          // Fit onto multiple pages if too tall.
          if (imgH <= pageH - 40) {
            pdf.addImage(imgData, "JPEG", 20, 20, imgW, imgH, undefined, "FAST");
          } else {
            // Slice: recompute ratio, then paginate by canvas Y offset.
            const ratio = imgW / out.width;
            const sliceHpx = (pageH - 40) / ratio;
            let y = 0;
            let first = true;
            while (y < out.height) {
              const slice = document.createElement("canvas");
              slice.width = out.width;
              slice.height = Math.min(sliceHpx, out.height - y);
              const sctx = slice.getContext("2d");
              sctx.fillStyle = "#02040A";
              sctx.fillRect(0, 0, slice.width, slice.height);
              sctx.drawImage(
                out,
                0, y, out.width, slice.height,
                0, 0, slice.width, slice.height,
              );
              const sliceData = slice.toDataURL("image/jpeg", 0.9);
              if (!first) pdf.addPage();
              pdf.addImage(sliceData, "JPEG", 20, 20, imgW, slice.height * ratio, undefined, "FAST");
              first = false;
              y += slice.height;
            }
          }
          pdf.save(`iarisk-chat-${ts}.pdf`);
          toast.success("PDF guardado");
        }
      } catch (err) {
        console.debug("[exportChat] failed", err);
        toast.error("No se pudo exportar la conversación.");
      } finally {
        setExporting(false);
      }
    },
    [messages, user],
  );

  // Smart routing for an arbitrary file (image/audio/doc)
  const handleAnyFile = useCallback(
    (file) => {
      if (!file) return;
      const t = (file.type || "").toLowerCase();
      const n = (file.name || "").toLowerCase();
      if (t.startsWith("image/")) return uploadImage(file);
      if (t.startsWith("audio/") || /\.(ogg|mp3|wav|m4a|aac|flac)$/i.test(n)) {
        return uploadAudio(file);
      }
      return uploadDoc(file);
    },
    [uploadImage, uploadAudio, uploadDoc]
  );

  // ------------- Drag & drop on the whole screen -------------
  useEffect(() => {
    const onDragOver = (e) => {
      if (e.dataTransfer?.types?.includes("Files")) {
        e.preventDefault();
        setDragOver(true);
      }
    };
    const onDragLeave = (e) => {
      if (e.target === document.documentElement || e.relatedTarget === null) {
        setDragOver(false);
      }
    };
    const onDrop = (e) => {
      e.preventDefault();
      setDragOver(false);
      const file = e.dataTransfer?.files?.[0];
      if (file) handleAnyFile(file);
    };
    window.addEventListener("dragover", onDragOver);
    window.addEventListener("dragleave", onDragLeave);
    window.addEventListener("drop", onDrop);
    return () => {
      window.removeEventListener("dragover", onDragOver);
      window.removeEventListener("dragleave", onDragLeave);
      window.removeEventListener("drop", onDrop);
    };
  }, [handleAnyFile]);

  // ------------- Paste image from clipboard -------------
  const onPaste = useCallback(
    (e) => {
      const items = e.clipboardData?.items || [];
      for (const it of items) {
        if (it.kind === "file") {
          const f = it.getAsFile();
          if (f) {
            e.preventDefault();
            handleAnyFile(f);
            return;
          }
        }
      }
    },
    [handleAnyFile]
  );

  if (loading || !user) {
    return (
      <div className="min-h-[100dvh] flex items-center justify-center bg-zinc-950 text-zinc-500 text-sm">
        Cargando…
      </div>
    );
  }

  const placeholder =
    mode === "chat"
      ? "Escribe, arrastra archivos o pega imágenes…"
      : mode === "image_gen"
      ? "Describe la imagen que quieres generar…"
      : "Pega la URL que quieres escanear…";

  return (
    <div className="h-[100dvh] flex bg-obsidian text-white relative overflow-hidden font-sans">
      {/* ===== Background effects layer (z-0 / z-1) ===== */}
      <div className="fixed inset-0 z-0 bg-obsidian pointer-events-none">
        <CodeRain density={0.25} speed={0.45} fade={0.10} />
      </div>

      {/* Gradient vignette for legibility */}
      <div
        className="fixed inset-0 pointer-events-none"
        style={{
          zIndex: 2,
          background:
            "radial-gradient(ellipse at center, transparent 0%, rgba(2,4,10,0.55) 55%, rgba(2,4,10,0.92) 100%), radial-gradient(ellipse at 20% 10%, rgba(255,26,58,0.10) 0%, transparent 50%)",
        }}
        aria-hidden="true"
      />

      {/* Everything functional sits above effects */}
      <div className="contents" style={{ position: "relative", zIndex: 10 }}>
      {/* ----- Left sidebar (desktop static + mobile drawer) ----- */}
      <aside
        className="hidden lg:flex w-72 flex-shrink-0 border-r border-[#00E5FF]/15 flex-col relative"
        style={{ zIndex: 10, background: "rgba(0,0,0,0.85)", backdropFilter: "blur(20px)" }}
        data-testid="left-sidebar-desktop"
      >
        <LeftSidebarContent
          user={user}
          mode={mode}
          setMode={setMode}
          triggerUpload={triggerUpload}
          navigate={navigate}
          botUsername={botUsername}
          onTelegramLinked={refreshCredits}
        />
      </aside>

      <Sheet open={sidebarOpen} onOpenChange={setSidebarOpen}>
        <SheetContent
          side="left"
          className="p-0 w-72 bg-black border-[#00E5FF]/30"
          data-testid="left-sidebar-drawer"
        >
          <SheetTitle className="sr-only">Menú principal</SheetTitle>
          <SheetDescription className="sr-only">
            Acceso a chat, imágenes, escaneo y otras funciones del bot.
          </SheetDescription>
          <LeftSidebarContent
            user={user}
            mode={mode}
            setMode={setMode}
            triggerUpload={triggerUpload}
            navigate={navigate}
            botUsername={botUsername}
            onClose={() => setSidebarOpen(false)}
            onTelegramLinked={refreshCredits}
          />
        </SheetContent>
      </Sheet>

      {/* ----- Main column ----- */}
      <div className="flex-1 min-w-0 flex flex-col relative" style={{ zIndex: 10 }}>
        {/* Header */}
        <header
          className="h-16 flex-shrink-0 bg-black/80 backdrop-blur-xl border-b border-[#00E5FF]/20 flex items-center px-3 sm:px-5 gap-2 relative"
          style={{ paddingTop: "env(safe-area-inset-top)" }}
        >
          {/* Top accent line */}
          <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#00E5FF] to-transparent" />

          <Button
            size="sm"
            variant="ghost"
            className="lg:hidden h-9 w-9 p-0 text-white hover:text-[#00E5FF] hover:bg-white/5"
            onClick={() => setSidebarOpen(true)}
            data-testid="open-sidebar-btn"
            aria-label="Abrir menú"
          >
            <MenuIcon size={18} />
          </Button>

          <div className="flex items-center gap-3 flex-1 min-w-0">
            <div className="w-9 h-9 bg-[#00E5FF] flex items-center justify-center flex-shrink-0 lg:hidden shadow-[0_0_14px_rgba(0,229,255,0.7)] clip-cyber">
              <span className="text-black font-black text-base">⚡</span>
            </div>
            <div className="min-w-0">
              <div className="text-sm font-cyber tracking-widest font-bold truncate text-white" style={{textShadow:"0 0 6px rgba(0,229,255,0.5)"}}>
                {(MODES.find((m) => m.id === mode)?.label || "Chat").toUpperCase()}
              </div>
              <div className="text-[10px] text-white/40 truncate font-terminal">
                {MODES.find((m) => m.id === mode)?.desc}
              </div>
            </div>
          </div>

          {/* Install PWA */}
          {canInstall && !isStandalone && (
            <Button
              onClick={async () => {
                const ok = await promptInstall();
                if (ok) toast.success("¡App instalada!");
              }}
              size="sm"
              variant="outline"
              className="border-[#2AABEE] text-[#2AABEE] hover:bg-[#2AABEE]/10 rounded-sm h-9"
              data-testid="install-pwa-btn"
            >
              <Download size={13} className="sm:mr-1" />
              <span className="hidden sm:inline">Instalar</span>
            </Button>
          )}

          {/* Push notifications toggle */}
          {pushSupported && pushPerm !== "denied" && (
            <Button
              onClick={async () => {
                if (pushOn) {
                  await pushDisable();
                  toast.success("Avisos desactivados");
                } else {
                  const r = await pushEnable();
                  if (r.ok) {
                    toast.success("🔔 Avisos activos — te notificaremos cobros, descargas y más");
                  } else {
                    toast.error(r.error || "No se pudo activar");
                  }
                }
              }}
              disabled={pushEnabling}
              size="sm"
              variant="outline"
              className={
                pushOn
                  ? "border-amber-500 text-amber-400 hover:bg-amber-500/10 rounded-sm h-9"
                  : "border-zinc-800 text-zinc-400 hover:text-amber-400 hover:border-amber-500/40 rounded-sm h-9"
              }
              data-testid={pushOn ? "push-on-btn" : "push-off-btn"}
              title={pushOn ? "Click para desactivar" : "Activar notificaciones"}
            >
              {pushOn ? <Bell size={13} className="sm:mr-1" /> : <BellOff size={13} className="sm:mr-1" />}
              <span className="hidden sm:inline text-xs">{pushOn ? "Avisos" : "Avisos"}</span>
            </Button>
          )}

          <div
            className="flex items-center gap-1.5 px-2 py-1 rounded-sm bg-zinc-900 border border-zinc-800"
            data-testid="web-credits-pill"
            title={`Créditos: ${user.credits ?? 0}${user.bonus_credits ? ` · Bono: ${user.bonus_credits}` : ""}`}
          >
            <Coins size={13} className="text-amber-400" />
            <span className="text-xs font-mono font-bold">
              {(user.credits ?? 0) + (user.bonus_credits ?? 0)}
            </span>
            <span className="text-[10px] text-zinc-500">cr</span>
            {(user.bonus_credits ?? 0) > 0 && (
              <span
                className="text-[9px] font-mono text-emerald-300 ml-1"
                title="Incluye bono restringido"
              >
                +{user.bonus_credits}
              </span>
            )}
          </div>

          {/* Share conversation dropdown */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                size="sm"
                variant="outline"
                className="border-fuchsia-500/40 text-fuchsia-300 hover:bg-fuchsia-500/10 hover:text-fuchsia-200 rounded-sm h-9"
                disabled={exporting || messages.length === 0}
                data-testid="share-chat-btn"
                title="Exportar / compartir esta conversación"
              >
                {exporting ? (
                  <Loader2 size={13} className="sm:mr-1 animate-spin" />
                ) : (
                  <Share2 size={13} className="sm:mr-1" />
                )}
                <span className="hidden sm:inline text-xs">Compartir</span>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              align="end"
              className="w-52 bg-zinc-950 border border-fuchsia-500/30 text-zinc-100"
              data-testid="share-menu"
            >
              <DropdownMenuLabel className="text-xs text-fuchsia-400">
                Exportar chat
              </DropdownMenuLabel>
              <DropdownMenuItem
                className="text-sm cursor-pointer focus:bg-fuchsia-500/10"
                onSelect={() => exportChat("png")}
                data-testid="share-as-png"
              >
                <FileImage size={14} className="mr-2 text-fuchsia-300" />
                Como imagen (PNG)
              </DropdownMenuItem>
              <DropdownMenuItem
                className="text-sm cursor-pointer focus:bg-fuchsia-500/10"
                onSelect={() => exportChat("pdf")}
                data-testid="share-as-pdf"
              >
                <FileDown size={14} className="mr-2 text-fuchsia-300" />
                Como PDF
              </DropdownMenuItem>
              <DropdownMenuSeparator className="bg-zinc-800" />
              <DropdownMenuItem
                className="text-sm cursor-pointer focus:bg-fuchsia-500/10"
                onSelect={async () => {
                  // Native share of the last exchange, if supported
                  const last = messages.slice(-2).map(m => `${m.role === "user" ? "TÚ" : "IARISK"}: ${m.content}`).join("\n\n");
                  const shareData = {
                    title: "IARisk chat",
                    text: `${last}\n\n— iarisk.com`,
                    url: "https://obsidiandesiremx.com",
                  };
                  try {
                    if (navigator.share) {
                      await navigator.share(shareData);
                    } else {
                      await navigator.clipboard.writeText(shareData.text);
                      toast.success("Copiado al portapapeles");
                    }
                  } catch (e) {
                    // user cancelled
                  }
                }}
                data-testid="share-native"
              >
                <Share2 size={14} className="mr-2 text-fuchsia-300" />
                Compartir enlace…
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>

          <Button
            onClick={() => navigate("/comprar")}
            size="sm"
            variant="outline"
            className="border-zinc-800 hover:bg-zinc-900 rounded-sm h-9"
            data-testid="web-buy-btn"
          >
            <ShoppingCart size={13} className="sm:mr-1" />
            <span className="hidden sm:inline">Comprar</span>
          </Button>

          <AccountMenu user={user} logout={logout} navigate={navigate} />
        </header>

        {/* Messages */}
        <div
          ref={messagesRef}
          className="flex-1 overflow-y-auto px-3 sm:px-6 py-4 max-w-3xl w-full mx-auto relative"
          onPaste={onPaste}
          data-testid="messages-container"
        >
          {messages.length === 0 && (
            <div className="text-center py-16 relative">
              <div className="inline-block mb-4">
                <Sparkles size={48} className="mx-auto text-[#00E5FF]" style={{filter:"drop-shadow(0 0 16px #00E5FF)"}} />
              </div>
              <div className="font-gothic font-black text-4xl mb-3 text-white tracking-widest" style={{textShadow:"0 0 18px rgba(0,229,255,0.7), 2px 2px 0 rgba(0,68,255,0.5)"}}>
                IARISK
              </div>
              <div className="text-xs font-cyber tracking-[0.4em] text-[#00E5FF] mb-6">
                // ASISTENTE NEURO-ELECTRIC ONLINE
              </div>
              <div className="text-sm max-w-md mx-auto text-white/70 font-terminal">
                Escribe, <span className="text-[#00E5FF] font-bold">arrastra archivos</span> aquí o{" "}
                <span className="text-[#00E5FF] font-bold">pega imágenes</span> con{" "}
                <kbd className="font-terminal text-xs px-2 py-1 bg-black border border-[#00E5FF]/50 text-[#00E5FF]">
                  Ctrl+V
                </kbd>
                .
              </div>
              <div className="text-[10px] mt-4 text-white/30 font-cyber tracking-widest">
                IMÁGENES · AUDIO · PDF · DOCX · CÓDIGO
              </div>
            </div>
          )}
          {messages.map((m) => (
            <Bubble key={m.id} {...m} />
          ))}
          {sending && (
            <div className="flex justify-start mb-5" data-testid="typing-indicator">
              <div className="glass-panel px-5 py-3.5 rounded-2xl rounded-tl-md flex items-center gap-1.5">
                <span className="typing-dot" />
                <span className="typing-dot" />
                <span className="typing-dot" />
                <span className="ml-2 text-[10px] font-cyber tracking-widest text-[#00E5FF]/70">
                  IARISK ESCRIBE
                </span>
              </div>
            </div>
          )}
          <div ref={bottomRef} />
        </div>

        {/* Mode pill bar */}
        <div
          className="flex-shrink-0 border-t border-[#00E5FF]/20 bg-black/80 backdrop-blur-xl px-3 sm:px-6 py-3 flex gap-2 overflow-x-auto relative"
          data-testid="mode-selector"
        >
          {MODES.map((m) => {
            const I = m.icon;
            const active = mode === m.id;
            return (
              <button
                key={m.id}
                onClick={() => setMode(m.id)}
                data-testid={`mode-${m.id}`}
                className={`flex items-center gap-1.5 px-3 py-1.5 text-[11px] font-cyber font-bold tracking-widest whitespace-nowrap transition-all ${
                  active
                    ? "bg-[#00E5FF] text-black shadow-[0_0_14px_rgba(0,229,255,0.6)]"
                    : "bg-transparent border border-white/15 text-white/60 hover:text-[#00E5FF] hover:border-[#00E5FF]/50"
                }`}
              >
                <I size={12} />
                {m.label.toUpperCase()}
              </button>
            );
          })}
        </div>

        {/* Composer */}
        <div
          ref={composerRef}
          className="flex-shrink-0 border-t border-[#00E5FF]/30 bg-black/85 backdrop-blur-xl px-3 sm:px-6 py-4 relative"
          style={{ paddingBottom: "calc(env(safe-area-inset-bottom) + 1rem)" }}
        >
          <div className="max-w-3xl mx-auto flex items-end gap-2">
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              className="hidden"
              onChange={(e) => {
                const f = e.target.files?.[0];
                e.target.value = "";
                if (f) uploadImage(f);
              }}
              data-testid="upload-image-input"
            />
            <input
              ref={docInputRef}
              type="file"
              accept=".pdf,.docx,.txt,.md,.csv,.json,.xml,.html,.css,.js,.jsx,.ts,.tsx,.py,.rb,.go,.rs,.java,.cpp,.c,.h,.cs,.php,.sh,.yaml,.yml,.toml,.ini,.log,.sql"
              className="hidden"
              onChange={(e) => {
                const f = e.target.files?.[0];
                e.target.value = "";
                if (f) uploadDoc(f);
              }}
              data-testid="upload-doc-input"
            />
            <input
              ref={audioInputRef}
              type="file"
              accept="audio/*"
              className="hidden"
              onChange={(e) => {
                const f = e.target.files?.[0];
                e.target.value = "";
                if (f) uploadAudio(f);
              }}
              data-testid="upload-audio-input"
            />

            {mode === "chat" && (
              <>
                <button
                  onClick={() => fileInputRef.current?.click()}
                  disabled={sending}
                  title="Analizar imagen"
                  data-testid="image-upload-btn"
                  className="w-10 h-10 bg-black border border-[#00E5FF]/30 hover:border-[#00E5FF] hover:shadow-[0_0_14px_rgba(0,229,255,0.5)] flex items-center justify-center flex-shrink-0 disabled:opacity-40 transition-all clip-cyber"
                >
                  <ImageIcon size={16} className="text-[#00E5FF]" />
                </button>
                <button
                  onClick={() => docInputRef.current?.click()}
                  disabled={sending}
                  title="Subir archivo / PDF"
                  data-testid="doc-upload-btn"
                  className="w-10 h-10 bg-black border border-[#00E5FF]/30 hover:border-[#00E5FF] hover:shadow-[0_0_14px_rgba(0,229,255,0.5)] flex items-center justify-center flex-shrink-0 disabled:opacity-40 transition-all clip-cyber"
                >
                  <Paperclip size={16} className="text-[#00E5FF]" />
                </button>
                <button
                  onClick={handleDictateClick}
                  disabled={sending || dictateUploading}
                  title={dictating ? "Detener dictado" : "Dictar por voz"}
                  data-testid="voice-dictate-btn"
                  className={`w-10 h-10 bg-black border ${
                    dictating
                      ? "border-rose-500 shadow-[0_0_18px_rgba(244,63,94,0.7)] animate-pulse"
                      : "border-[#00E5FF]/30 hover:border-[#00E5FF] hover:shadow-[0_0_14px_rgba(0,229,255,0.5)]"
                  } flex items-center justify-center flex-shrink-0 disabled:opacity-40 transition-all clip-cyber relative`}
                >
                  {dictateUploading ? (
                    <Loader2 size={16} className="text-[#00E5FF] animate-spin" />
                  ) : dictating ? (
                    <MicOff size={16} className="text-rose-400" strokeWidth={2.5} />
                  ) : (
                    <Mic size={16} className="text-[#00E5FF]" />
                  )}
                  {dictating && (
                    <span
                      className="absolute -bottom-0.5 left-1/2 -translate-x-1/2 h-0.5 bg-rose-400 transition-all"
                      style={{ width: `${Math.max(6, dictateLevel * 32)}px` }}
                    />
                  )}
                </button>
                {/* Push-to-talk Voice Assistant (Premium · 5 créditos) */}
                <button
                  onPointerDown={ptStart}
                  onPointerUp={ptStop}
                  onPointerLeave={(e) => {
                    if (assistant.recording) ptStop(e);
                  }}
                  onPointerCancel={(e) => {
                    if (assistant.recording) ptStop(e);
                  }}
                  onContextMenu={(e) => e.preventDefault()}
                  disabled={sending || assistant.sending}
                  title={
                    assistant.recording
                      ? "Suelta para enviar"
                      : "MANTÉN PRESIONADO para hablar con el asistente (premium · 5 créditos)"
                  }
                  data-testid="voice-assistant-ptt-btn"
                  className={`w-10 h-10 bg-black border ${
                    assistant.recording
                      ? "border-fuchsia-500 shadow-[0_0_22px_rgba(217,70,239,0.85)] scale-110 animate-pulse"
                      : "border-fuchsia-500/40 hover:border-fuchsia-500 hover:shadow-[0_0_16px_rgba(217,70,239,0.6)]"
                  } flex items-center justify-center flex-shrink-0 disabled:opacity-40 transition-all clip-cyber relative select-none touch-none`}
                  style={{ WebkitUserSelect: "none", userSelect: "none" }}
                >
                  {assistant.sending ? (
                    <Loader2 size={16} className="text-fuchsia-400 animate-spin" />
                  ) : (
                    <Bot
                      size={16}
                      className={
                        assistant.recording ? "text-fuchsia-300" : "text-fuchsia-400"
                      }
                      strokeWidth={2.5}
                    />
                  )}
                  {assistant.recording && (
                    <span
                      className="absolute -bottom-0.5 left-1/2 -translate-x-1/2 h-0.5 bg-fuchsia-400 transition-all"
                      style={{ width: `${Math.max(6, assistant.level * 32)}px` }}
                    />
                  )}
                </button>
              </>
            )}

            <textarea
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  send();
                }
              }}
              onPaste={onPaste}
              placeholder={placeholder}
              rows={1}
              data-testid="composer-input"
              className="flex-1 bg-black border-2 border-[#00E5FF]/30 px-4 py-2.5 text-sm font-terminal text-white placeholder:text-white/30 resize-none focus:outline-none focus:border-[#00E5FF] focus:shadow-[0_0_18px_rgba(0,229,255,0.4)] focus:ring-0 max-h-32 clip-cyber"
              style={{ minHeight: "40px" }}
            />
            <Button
              onClick={send}
              disabled={sending || !input.trim()}
              data-testid="composer-send-btn"
              className="bg-[#00E5FF] hover:bg-white text-black hover:text-black font-bold h-10 px-4 flex-shrink-0 shadow-[0_0_18px_rgba(0,229,255,0.6)] hover:shadow-[0_0_28px_rgba(255,255,255,0.8)] transition-all clip-cyber"
            >
              {sending ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Send size={16} strokeWidth={2.5} />
              )}
            </Button>
          </div>
        </div>
      </div>

      {/* ---- Drag & drop overlay ---- */}
      {dragOver && (
        <div
          className="fixed inset-0 z-50 bg-[#2AABEE]/15 backdrop-blur-sm border-4 border-dashed border-[#2AABEE] flex items-center justify-center pointer-events-none"
          data-testid="drag-overlay"
        >
          <div className="bg-black border-2 border-[#00E5FF] p-10 text-center shadow-[0_0_60px_rgba(0,229,255,0.6)] clip-cyber">
            <Paperclip size={42} className="mx-auto mb-4 text-[#00E5FF]" style={{filter:"drop-shadow(0 0 10px #00E5FF)"}} />
            <div className="text-2xl font-gothic font-black mb-2 text-white tracking-widest" style={{textShadow:"0 0 12px rgba(0,229,255,0.8)"}}>
              SUELTA PARA ANALIZAR
            </div>
            <div className="text-xs text-[#00E5FF]/80 font-cyber tracking-widest">
              // IMÁGENES · PDF · DOCX · AUDIO · CÓDIGO
            </div>
          </div>
        </div>
      )}
      </div>
    </div>
  );
}

// Re-export anonymous menu for landing reuse
export { AnonAccountMenu };
